import { useEffect } from 'react';
import { updateWorkflow } from 'Services/storeService';
import { message } from 'antd';
import { serializeGraph } from '../constants';
import logger from 'utils/logger';

/**
 * Runs a 5-second interval that compares the current graph to the last-saved
 * snapshot. Only triggers an API call when the graph has actually changed.
 *
 * The email/SMS template content (emailContent, mjmlContent, smsContent) is
 * deliberately excluded from serializeGraph, so editing those in the respective
 * editors CANNOT trigger this auto-save.
 *
 * `enabled` is false for roles without communications.manage: the interval is
 * never started, so a view-only user can never trigger a write (and the 403
 * logout that would follow).
 */
const useAutoSave = ({
  workflowIdRef,
  nodesRef,
  edgesRef,
  isSavingRef,
  isFlowLoadedRef,
  lastSavedGraphRef,
  setSaveStatus,
  setIsSaving,
  setLastSavedAt,
  setWorkflowVersion,
  enabled = true,
}) => {
  useEffect(() => {
    if (!enabled) return undefined;

    const intervalId = setInterval(async () => {
      const wfId = workflowIdRef.current;
      const currentNodes = nodesRef.current;
      const currentEdges = edgesRef.current;

      // Skip when: no workflow, empty canvas, already saving, or flow not yet loaded
      if (!wfId || currentNodes.length === 0 || isSavingRef.current || !isFlowLoadedRef.current) return;

      const currentGraph = serializeGraph(currentNodes, currentEdges);

      // No changes detected — skip save
      if (currentGraph === lastSavedGraphRef.current) return;

      // ────────────────────────────────────────────────────────────────────


      isSavingRef.current = true;
      setSaveStatus('saving');
      setIsSaving(true);

      const payload = {
        graph_json: {
          nodes: currentNodes.map((n) => ({
            id: n.id,
            type: n.type,
            position: n.position,
            style: n.style,
            // Only persist templateId — actual content lives in the template API
            data: (({ emailContent, mjmlContent, smsContent, ...rest }) => rest)(n.data || {}),
          })),
          edges: currentEdges,
        },
        status: 'draft',
      };

      try {
        await updateWorkflow(wfId, payload);
        lastSavedGraphRef.current = currentGraph;
        setLastSavedAt(new Date());
        setSaveStatus('saved');
        setWorkflowVersion('draft');
      } catch (error) {
        logger.error('[AutoSave] Auto-save failed:', error);
        setSaveStatus('unsaved');
        message.error('Failed to auto-save draft');
      } finally {
        isSavingRef.current = false;
        setIsSaving(false);
      }
    }, 5000);

    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);
};

export default useAutoSave;
